import * as api from "./client";
import type { ImageRef } from "./filesUrl";

export type { ImageRef };

export type EditionStatus = "draft" | "published" | "archived";
export type EntryStatus = "draft" | "published";
export type RegionKind = "region" | "country";
export type ListKind = "individuals" | "teams" | "rising_stars" | "hall_of_fame";

export const LIST_KIND_LABELS: Record<ListKind, string> = {
  individuals: "Individuals",
  teams: "Teams",
  rising_stars: "Rising Stars",
  hall_of_fame: "Hall of Fame",
};

export const LIST_KINDS = Object.keys(LIST_KIND_LABELS) as ListKind[];

export type Series = {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
};

export type Edition = {
  id: string;
  seriesId: string;
  year: number;
  title: string;
  slug: string;
  status: EditionStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type EditionRegion = {
  editionId: string;
  regionKind: RegionKind;
  regionRefId: number;
  displayLabel: string;
  createdAt: string;
  updatedAt: string;
};

export type PowerlistList = {
  id: string;
  editionId: string;
  kind: ListKind;
  title: string;
  description: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type SponsorTier = "partnered_with" | "headline_partner" | "associate_partner";

export type Sponsor = {
  id: string;
  editionId: string;
  listId: string | null;
  companyId: string | null;
  displayName: string;
  logo: ImageRef | null;
  tier: SponsorTier;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type Badge = { id: string; name: string; slug: string };

export type Entry = {
  id: string;
  listId: string;
  editionId: string;
  slug: string;
  status: EntryStatus;
  name: string;
  surname: string | null;
  jobTitle: string | null;
  companyId: string | null;
  companyName: string | null;
  photo: ImageRef | null;
  logo: ImageRef | null;
  industrySectorId: string | null;
  biography: string | null;
  teamSize: string | null;
  majorLegalAdvisors: string | null;
  jurisdictionsCovered: string | null;
  badges: Badge[];
  createdAt: string;
  updatedAt: string;
};

export type Question = {
  id: string;
  text: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type EditionQuestion = {
  editionId: string;
  questionId: string;
  text: string;
  isMandatory: boolean;
  sortOrder: number;
};

export type EntryAnswer = {
  id: string;
  entryId: string;
  questionId: string;
  answer: string;
  isVisible: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type SponsorSummary = {
  id: string;
  companyId: string | null;
  displayName: string;
  logo: ImageRef | null;
  tier: SponsorTier;
  sortOrder: number;
};

export type PreviewEntry = {
  id: string;
  slug: string;
  status: EntryStatus;
  name: string;
  jobTitle: string | null;
  companyName: string | null;
  photo: ImageRef | null;
  logo: ImageRef | null;
  industrySectorId: string | null;
  industrySectorName: string | null;
  badges: Badge[];
  hasInterview: boolean;
};

export type PreviewList = {
  id: string;
  kind: ListKind;
  title: string;
  description: string | null;
  sortOrder: number;
  sponsors: SponsorSummary[];
  entries: PreviewEntry[];
};

export type EditionPreview = {
  edition: Edition;
  series: { id: string; name: string; slug: string };
  displayLabel: string | null;
  regions: Array<{ regionKind: RegionKind; regionRefId: number; displayLabel: string }>;
  sponsors: SponsorSummary[];
  lists: PreviewList[];
};

export type SeriesInput = { name: string; slug?: string };
export type EditionPublicPreviewLink = { url: string; expiresAt: string };

export type EditionCreateInput = { seriesId: string; year: number; title?: string; slug?: string };
export type EditionUpdateInput = { year?: number; title?: string; slug?: string };
export type EditionRegionInput = {
  regionKind: RegionKind;
  regionRefId: number;
  displayLabel: string;
};
export type ListCreateInput = {
  kind: ListKind;
  title?: string;
  description?: string | null;
  sortOrder?: number;
};
export type ListUpdateInput = Omit<ListCreateInput, "kind">;
export type SponsorInput = {
  listId: string | null;
  companyId?: string | null;
  displayName: string;
  logo: ImageRef | null;
  tier?: SponsorTier;
  sortOrder?: number;
};
export type EntryFields = {
  name: string;
  surname: string | null;
  slug?: string;
  jobTitle: string | null;
  companyId: string | null;
  companyName: string | null;
  photo: ImageRef | null;
  logo: ImageRef | null;
  industrySectorId: string | null;
  biography: string | null;
  teamSize: string | null;
  majorLegalAdvisors: string | null;
  jurisdictionsCovered: string | null;
};
export type EntryUpdateInput = Partial<EntryFields> & { listId?: string; status?: EntryStatus };
export type CopySkipReason = "no_matching_list" | "already_exists";
export type CopyEntriesResult = {
  copied: number;
  skipped: Array<{ entryId: string; name: string; reason: CopySkipReason }>;
};
export type EditionQuestionInput = { isMandatory?: boolean; sortOrder?: number };
export type AnswerInput = { answer: string; isVisible?: boolean; sortOrder?: number };

const BASE = "/v1/powerlist";
const PAGE_SIZE = 500;
const BULK_STATUS_LIMIT = 500;
const COPY_LIMIT = 500;

export class PartialBatchError<T> extends Error {
  readonly completed: T;
  readonly failure: unknown;

  constructor(completed: T, failure: unknown) {
    super(failure instanceof Error ? failure.message : String(failure));
    this.name = "PartialBatchError";
    this.completed = completed;
    this.failure = failure;
  }
}

type Paginated = { pagination: { hasMore: boolean } };

async function fetchAllPages<T>(
  path: string,
  params: URLSearchParams,
  pick: (response: Paginated & Record<string, unknown>) => T[],
): Promise<T[]> {
  const all: T[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));
    const response = await api.get<Paginated & Record<string, unknown>>(`${path}?${params}`);
    all.push(...pick(response));
    if (!response.pagination.hasMore) return all;
  }
}

export const powerlistSeries = {
  list: async (): Promise<Series[]> =>
    (await api.get<{ series: Series[] }>(`${BASE}/series`)).series,
  get: async (id: string): Promise<Series> =>
    (await api.get<{ series: Series }>(`${BASE}/series/${id}`)).series,
  create: async (input: SeriesInput): Promise<Series> =>
    (await api.post<{ series: Series }>(`${BASE}/series`, input)).series,
  update: async (id: string, input: Partial<SeriesInput>): Promise<Series> =>
    (await api.put<{ series: Series }>(`${BASE}/series/${id}`, input)).series,
  remove: (id: string): Promise<void> => api.del<void>(`${BASE}/series/${id}`),
};

export const powerlistEditions = {
  listForSeries: (seriesId: string): Promise<Edition[]> =>
    fetchAllPages(
      `${BASE}/editions`,
      new URLSearchParams({ seriesId }),
      response => response.editions as Edition[],
    ),
  get: async (id: string): Promise<Edition> =>
    (await api.get<{ edition: Edition }>(`${BASE}/editions/${id}`)).edition,
  create: async (input: EditionCreateInput): Promise<Edition> =>
    (await api.post<{ edition: Edition }>(`${BASE}/editions`, input)).edition,
  update: async (id: string, input: EditionUpdateInput): Promise<Edition> =>
    (await api.put<{ edition: Edition }>(`${BASE}/editions/${id}`, input)).edition,
  remove: (id: string): Promise<void> => api.del<void>(`${BASE}/editions/${id}`),
  transition: async (
    id: string,
    action: "publish" | "unpublish" | "archive",
  ): Promise<{ edition: Edition; entriesPublished: number }> =>
    api.post<{ edition: Edition; entriesPublished: number }>(`${BASE}/editions/${id}/${action}`),
  preview: async (id: string): Promise<EditionPreview> =>
    (await api.get<{ preview: EditionPreview }>(`${BASE}/editions/${id}/preview`)).preview,
  createPublicPreviewLink: (id: string): Promise<EditionPublicPreviewLink> =>
    api.post<EditionPublicPreviewLink>(`${BASE}/editions/${id}/public-preview-link`),
};

export const powerlistRegions = {
  list: async (editionId: string): Promise<EditionRegion[]> =>
    (await api.get<{ regions: EditionRegion[] }>(`${BASE}/editions/${editionId}/regions`)).regions,
  create: async (editionId: string, input: EditionRegionInput): Promise<EditionRegion> =>
    (await api.post<{ region: EditionRegion }>(`${BASE}/editions/${editionId}/regions`, input))
      .region,
  update: async (region: EditionRegion, displayLabel: string): Promise<EditionRegion> =>
    (
      await api.put<{ region: EditionRegion }>(
        `${BASE}/editions/${region.editionId}/regions/${region.regionKind}/${region.regionRefId}`,
        { displayLabel },
      )
    ).region,
  remove: (region: EditionRegion): Promise<void> =>
    api.del<void>(
      `${BASE}/editions/${region.editionId}/regions/${region.regionKind}/${region.regionRefId}`,
    ),
};

export const powerlistLists = {
  list: async (editionId: string): Promise<PowerlistList[]> =>
    (await api.get<{ lists: PowerlistList[] }>(`${BASE}/editions/${editionId}/lists`)).lists,
  create: async (editionId: string, input: ListCreateInput): Promise<PowerlistList> =>
    (await api.post<{ list: PowerlistList }>(`${BASE}/editions/${editionId}/lists`, input)).list,
  update: async (id: string, input: ListUpdateInput): Promise<PowerlistList> =>
    (await api.put<{ list: PowerlistList }>(`${BASE}/lists/${id}`, input)).list,
  remove: (id: string): Promise<void> => api.del<void>(`${BASE}/lists/${id}`),
};

export const powerlistSponsors = {
  list: async (editionId: string): Promise<Sponsor[]> =>
    (await api.get<{ sponsors: Sponsor[] }>(`${BASE}/editions/${editionId}/sponsors`)).sponsors,
  create: async (editionId: string, input: SponsorInput): Promise<Sponsor> =>
    (await api.post<{ sponsor: Sponsor }>(`${BASE}/editions/${editionId}/sponsors`, input)).sponsor,
  update: async (id: string, input: Partial<SponsorInput>): Promise<Sponsor> =>
    (await api.put<{ sponsor: Sponsor }>(`${BASE}/sponsors/${id}`, input)).sponsor,
  remove: (id: string): Promise<void> => api.del<void>(`${BASE}/sponsors/${id}`),
};

export const powerlistEntries = {
  listForEdition: (editionId: string): Promise<Entry[]> =>
    fetchAllPages(
      `${BASE}/entries`,
      new URLSearchParams({ editionId }),
      response => response.entries as Entry[],
    ),
  get: async (id: string): Promise<Entry> =>
    (await api.get<{ entry: Entry }>(`${BASE}/entries/${id}`)).entry,
  create: async (listId: string, input: EntryFields): Promise<Entry> =>
    (await api.post<{ entry: Entry }>(`${BASE}/lists/${listId}/entries`, input)).entry,
  update: async (id: string, input: EntryUpdateInput): Promise<Entry> =>
    (await api.put<{ entry: Entry }>(`${BASE}/entries/${id}`, input)).entry,
  remove: (id: string): Promise<void> => api.del<void>(`${BASE}/entries/${id}`),
  bulkStatus: async (
    editionId: string,
    entryIds: string[],
    status: EntryStatus,
  ): Promise<number> => {
    let updated = 0;
    for (let start = 0; start < entryIds.length; start += BULK_STATUS_LIMIT) {
      try {
        const response = await api.post<{ updated: number }>(
          `${BASE}/editions/${editionId}/entries/bulk-status`,
          { entryIds: entryIds.slice(start, start + BULK_STATUS_LIMIT), status },
        );
        updated += response.updated;
      } catch (error) {
        throw start === 0 ? error : new PartialBatchError(updated, error);
      }
    }
    return updated;
  },
  copyFromEdition: async (
    editionId: string,
    sourceEditionId: string,
    entryIds: string[],
  ): Promise<CopyEntriesResult> => {
    const result: CopyEntriesResult = { copied: 0, skipped: [] };
    for (let start = 0; start < entryIds.length; start += COPY_LIMIT) {
      try {
        const response = await api.post<CopyEntriesResult>(
          `${BASE}/editions/${editionId}/entries/copy`,
          { sourceEditionId, entryIds: entryIds.slice(start, start + COPY_LIMIT) },
        );
        result.copied += response.copied;
        result.skipped.push(...response.skipped);
      } catch (error) {
        throw start === 0 ? error : new PartialBatchError(result, error);
      }
    }
    return result;
  },
};

export type EntryCompany = { id: string; name: string };

const ENTRY_COMPANY_SEARCH_LIMIT = 20;

export async function searchEntryCompanies(query: string): Promise<EntryCompany[]> {
  const q = query.trim();
  if (!q) return [];
  const response = await api.get<{ companies: Array<EntryCompany & { firmRef: string | null }> }>(
    `${BASE}/website-companies/search?${new URLSearchParams({ q, limit: String(ENTRY_COMPANY_SEARCH_LIMIT) })}`,
  );
  return response.companies.map(({ id, name }) => ({ id, name }));
}

export const powerlistBadges = {
  list: async (): Promise<Badge[]> => (await api.get<{ badges: Badge[] }>(`${BASE}/badges`)).badges,
  add: (entryId: string, badgeId: string): Promise<unknown> =>
    api.post(`${BASE}/entries/${entryId}/badges`, { badgeId }),
  remove: (entryId: string, badgeId: string): Promise<void> =>
    api.del<void>(`${BASE}/entries/${entryId}/badges/${badgeId}`),
};

export const powerlistQuestions = {
  list: async (): Promise<Question[]> =>
    (await api.get<{ questions: Question[] }>(`${BASE}/questions`)).questions,
  create: async (input: { text: string; isActive?: boolean }): Promise<Question> =>
    (await api.post<{ question: Question }>(`${BASE}/questions`, input)).question,
  update: async (id: string, input: { text?: string; isActive?: boolean }): Promise<Question> =>
    (await api.put<{ question: Question }>(`${BASE}/questions/${id}`, input)).question,
  remove: (id: string): Promise<void> => api.del<void>(`${BASE}/questions/${id}`),
};

export const powerlistEditionQuestions = {
  list: async (editionId: string): Promise<EditionQuestion[]> =>
    (
      await api.get<{ editionQuestions: EditionQuestion[] }>(
        `${BASE}/editions/${editionId}/questions`,
      )
    ).editionQuestions,
  add: async (
    editionId: string,
    input: EditionQuestionInput & { questionId: string },
  ): Promise<EditionQuestion> =>
    (
      await api.post<{ editionQuestion: EditionQuestion }>(
        `${BASE}/editions/${editionId}/questions`,
        input,
      )
    ).editionQuestion,
  update: async (
    editionId: string,
    questionId: string,
    input: EditionQuestionInput,
  ): Promise<EditionQuestion> =>
    (
      await api.put<{ editionQuestion: EditionQuestion }>(
        `${BASE}/editions/${editionId}/questions/${questionId}`,
        input,
      )
    ).editionQuestion,
  remove: (editionId: string, questionId: string): Promise<void> =>
    api.del<void>(`${BASE}/editions/${editionId}/questions/${questionId}`),
};

export const powerlistAnswers = {
  list: async (entryId: string): Promise<EntryAnswer[]> =>
    (await api.get<{ answers: EntryAnswer[] }>(`${BASE}/entries/${entryId}/answers`)).answers,
  upsert: async (entryId: string, questionId: string, input: AnswerInput): Promise<EntryAnswer> =>
    (
      await api.put<{ answer: EntryAnswer }>(
        `${BASE}/entries/${entryId}/answers/${questionId}`,
        input,
      )
    ).answer,
  remove: (entryId: string, questionId: string): Promise<void> =>
    api.del<void>(`${BASE}/entries/${entryId}/answers/${questionId}`),
};

export const POWERLIST_IMAGE_UPLOAD_ENDPOINT = `${BASE}/images`;
